/**
 * Session record of the language the user EXPLICITLY picked in the UI this session, via the
 * `/login` + `/register` `LanguagePicker`, the configurator `UserMenu` dropdown, or Settings.
 *
 * Why a dedicated key (NOT the existing `ows-start-locale-override`): that key is the
 * `startProperties` marketing hand-off locale, and `nova/config/localeRegion.ts`
 * (`readHandoffLocale` / `handoffRegion`) ALSO derives the configurator's deploy-region
 * pre-select from it. Writing a plain language pick there would silently re-pick the market.
 * This key carries language intent ONLY.
 *
 * Purpose: a language chosen on `/login` / `/register` is picked BEFORE authentication, so there
 * is no token yet to persist it to the cross-device user-preferences store. Recording it here
 * lets the flow carry the choice across the auth transition so, once authenticated:
 *  - the configurator entry (the `useStartProperties` hook) FLUSHES it to user-preferences,
 *    overwriting whatever default the freshly-minted account carries, because the UI choice is
 *    the source of truth; and
 *  - the app-wide saved-language adoption (`nova/theme/OwsTheme`) does NOT revert the explicit
 *    pick back to that stale/default fetched pref.
 *
 * Session-scoped and best-effort (private mode / disabled storage just degrades to today's
 * behaviour). Cleared on session teardown, logout AND impersonation identity-change
 * (`store/index.ts`), so one owner's pick never bleeds into the next on a shared browser.
 */
export const MANUAL_LANGUAGE_PICK_KEY = 'ows-manual-language-pick';

/** Record that the user explicitly picked `code` as their UI language this session. */
export function markManualLanguagePick(code: string): void {
  try {
    sessionStorage.setItem(MANUAL_LANGUAGE_PICK_KEY, code);
  } catch {
    /* sessionStorage unavailable, so the pick just isn't carried across auth (best effort). */
  }
}

/** The language the user explicitly picked this session, or `null` when none / unavailable. */
export function readManualLanguagePick(): string | null {
  try {
    return sessionStorage.getItem(MANUAL_LANGUAGE_PICK_KEY);
  } catch {
    return null;
  }
}

/** Drop the recorded pick (session teardown), so it never carries into the next owner's session. */
export function clearManualLanguagePick(): void {
  try {
    sessionStorage.removeItem(MANUAL_LANGUAGE_PICK_KEY);
  } catch {
    /* nothing to clear. */
  }
}
